import { Col, Row } from "react-bootstrap";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useGet, usePost } from "@/apiHooks/HttpFunctions";
import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import RenderToast from "@/components/RenderToast";
import { settingsApi } from "@/apis/apis";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import getSettingInitialValues from "@/data/SettingManagement/initial-values";
import getSettingSchema from "@/schema/SettingManagement/schema";

const SettingManagementModal = forwardRef((_, ref) => {
  const [modalState, setModalState] = useState({ isOpen: false, id: null });
  const showApi = modalState.id ? `${settingsApi}/${modalState.id}` : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Language ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePost({
    apiURL: `${settingsApi}/${modalState.id}`,
    isFormData: true,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: settingsApi,
    isFormData: true,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getSettingInitialValues(modalState.id ? showData : null),
    validationSchema: getSettingSchema(!!modalState.id),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = (values) => {
    const formData = new FormData();
    if (modalState.id) {
      formData.append("_method", "PUT");
    }

    formData.append("name", values.name);
    formData.append("value", values.value);
    formData.append("description", values.description);

    modalState.id ? saveEdit(formData) : saveCreate(formData);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={modalState.id ? "Edit Setting" : "Add Setting"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"name"}
            saveError={saveError}
            label="Name"
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"value"}
            saveError={saveError}
            label="Value"
            type={showData?.data?.type}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"description"}
            saveError={saveError}
            label="Description"
            type="textarea"
            rows={3}
          />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default SettingManagementModal;
